import { useEffect, useState } from 'react';
import type { WorkItem } from '../components/workitems/workItemTypes';
import type { CheckoutDialogInvoice } from '../components/payments/CheckoutDialog';
import { getWorkItemByNumber } from '../services/workItemDataService';
import { getCustomerIndexCached } from '../services/customerIndexCacheService';

/**
 * Resolves a scanned ticket QR (deep-link → ticket number) into everything the
 * mobile pickup flow needs: the work item, its outstanding invoice (shaped for
 * the shared payment hooks), and a settled/unsettled verdict.
 *
 * Resolution path (all confirmed APIs, no new endpoint — D4):
 *   ticket number → getWorkItemByNumber → WorkItem (status, customer, invoices[])
 *   WorkItem.customerId (DV GUID) → customer index → repairShoprCustomerId
 * The rsCustomerId is required by CheckoutDialogInvoice / useCreatePayment.
 */
export interface PickupResolution {
  workItem: WorkItem;
  guid: string;
  ticketNumber: number;
  jobStatus: string;
  customerLabel: string;
  /** First outstanding invoice, shaped for the payment hooks; null when settled. */
  invoice: CheckoutDialogInvoice | null;
  /** Total outstanding across all unpaid invoices on the ticket. */
  balanceDue: number;
  /** Number of unpaid invoices — >1 flags a not-yet-supported multi-invoice ticket. */
  unpaidCount: number;
  /** True when nothing is owed (paid, prepaid, or no invoice) → straight to collect. */
  isSettled: boolean;
}

export interface UseScanPickupResolutionResult {
  data: PickupResolution | null;
  isLoading: boolean;
  error: string | null;
}

export function useScanPickupResolution(
  ticketNumber: number | null,
): UseScanPickupResolutionResult {
  const [data, setData] = useState<PickupResolution | null>(null);
  const [isLoading, setIsLoading] = useState(ticketNumber != null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (ticketNumber == null || !Number.isFinite(ticketNumber)) {
      setData(null);
      setIsLoading(false);
      setError(ticketNumber == null ? null : 'Invalid ticket number');
      return;
    }

    const controller = new AbortController();
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        const workItem = await getWorkItemByNumber(ticketNumber, controller.signal);
        if (controller.signal.aborted) return;
        if (!workItem) {
          setError(`No ticket found for #${ticketNumber}`);
          setData(null);
          return;
        }

        // Resolve rsCustomerId from the customer index via the DV customer GUID.
        let rsCustomerId: number | null = null;
        try {
          const index = await getCustomerIndexCached();
          const match = index.items.find(
            (c) => c.dataverseCustomerId === workItem.customerId,
          );
          const rs = match?.repairShoprCustomerId;
          rsCustomerId = rs ? Number(rs) || null : null;
        } catch {
          // Index unavailable — checkout can't proceed, but resolution still
          // renders the ticket + status. Left as null; the page guards on it.
        }
        if (controller.signal.aborted) return;

        const unpaid = (workItem.invoices ?? []).filter(
          (inv) => !inv.is_paid && inv.balance_due > 0.001,
        );
        const balanceDue = unpaid.reduce((sum, inv) => sum + inv.balance_due, 0);

        const first = unpaid[0];
        const invoice: CheckoutDialogInvoice | null =
          first && rsCustomerId != null
            ? {
                rsInvoiceId: first.id,
                rsCustomerId,
                number: first.number,
                balanceDue: first.balance_due,
              }
            : null;

        setData({
          workItem,
          guid: workItem.guid,
          ticketNumber,
          jobStatus: workItem.jobStatus || workItem.status || '',
          customerLabel:
            workItem.customerBusinessName || workItem.customerName || 'Customer',
          invoice,
          balanceDue,
          unpaidCount: unpaid.length,
          isSettled: unpaid.length === 0,
        });
      } catch (e) {
        if ((e as DOMException)?.name === 'AbortError') return;
        setError(e instanceof Error ? e.message : 'Failed to resolve ticket');
        setData(null);
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    })();

    return () => controller.abort();
  }, [ticketNumber]);

  return { data, isLoading, error };
}
